/**
 * El proyecto activo.
 *
 * Sin manuscrito elegido no se encarga nada. No es una restricción caprichosa: el motor guarda
 * los entregables donde se le diga, y si no se le dice nada acaban en el Escritorio, sueltos y
 * lejos del texto al que pertenecen. Un dossier del autor sin su carpeta es papel perdido.
 *
 * CÓMO SE ELIGE. El navegador no puede darnos una ruta: un `<input type="file">` entrega el
 * contenido, nunca dónde vive. Pero el MOTOR corre en el ordenador del autor, así que el diálogo
 * lo abre él —el selector de siempre, el del sistema— y devuelve la ruta.
 *
 * Y se elige EL DOCUMENTO, no la carpeta. Es lo que la persona tiene en la cabeza: «mi novela es
 * este fichero». La carpeta se deduce de dónde está, que es lo que el motor necesita para dejar
 * los entregables al lado del texto. Antes había que buscar la carpeta en un explorador nuestro
 * que solo enseñaba carpetas y no dejaba ver los ficheros: poco familiar y una pregunta que el
 * autor no se hace.
 *
 * Los otros dos caminos se quedan de respaldo, en este orden: elegir la carpeta entera, y teclear
 * la ruta. Si el selector del sistema no se puede abrir, se dice y quedan los dos.
 */
import { useState } from "react";
import { proyecto, elegirDelSistema } from "../api";
import Explorador from "./Explorador";
import type { Fichero } from "../api";
import { carpetaDe, problemaDeCarpeta } from "../logica";

export default function Proyecto(
  { dir, alElegir }: { dir: string; alElegir: (d: string, f: Fichero[], doc?: string) => void },
) {
  const [texto, setTexto] = useState(dir);
  const [mirando, setMirando] = useState(false);
  const [abriendo, setAbriendo] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  const [pidiendo, setPidiendo] = useState(false);

  /**
   * Abre el selector del sistema y se queda con la carpeta.
   *
   * Si se elige un documento, la carpeta es la suya: `dirname`. Se hace aquí y no en el motor
   * porque el motor devuelve lo que el autor eligió, sin interpretarlo — y la bitácora anota esa
   * elección tal cual, que es lo que después permite decir qué se abrió.
   */
  async function abrirNativo(tipo: "documento" | "carpeta") {
    setPidiendo(true);
    setFallo(null);
    try {
      const r = await elegirDelSistema(tipo, texto.trim() || undefined);
      if (r.cancelado || !r.ruta) return;                 // cerró el diálogo: no es un error
      // Se corta por la última barra, sea del tipo que sea: en Windows el motor devuelve la
      // ruta con barras invertidas y un recorte que solo mire «/» dejaría el nombre del
      // fichero pegado a la carpeta.
      const carpeta = tipo === "documento" ? carpetaDe(r.ruta) : r.ruta;
      setTexto(carpeta);
      await comprobar(carpeta, tipo === "documento" ? r.ruta : undefined);
    } catch (e) {
      // El selector no se pudo abrir. No se deja al autor sin salida: se le ofrece el explorador
      // y el campo de texto, que es peor experiencia pero funciona.
      setFallo(e instanceof Error ? e.message : "No se pudo abrir el selector del sistema.");
      setAbriendo(true);
    } finally {
      setPidiendo(false);
    }
  }

  // `doc` es el fichero que el autor SEÑALÓ. Se propaga hasta arriba porque, sin él, el panel
  // adivina cuál es el manuscrito —cogía el más grande de la carpeta— y en una carpeta como
  // Descargas la adivinanza es siempre otro libro.
  async function comprobar(ruta?: string, doc?: string) {
    const d = (ruta ?? texto).trim();
    if (!d) return;
    setMirando(true);
    setFallo(null);
    try {
      const r = await proyecto(d);
      if ("error" in r) throw new Error(r.error);
      // Tres cosas distintas que antes se contaban igual. «No hay documentos» mandaba al autor a
      // buscar el fallo dentro de una carpeta que a veces ni existía.
      const problema = problemaDeCarpeta(r);
      if (problema) throw new Error(problema);
      alElegir(r.root, r.files, doc);
    } catch (e) {
      setFallo(e instanceof Error ? e.message : "No se pudo leer esa carpeta.");
    } finally {
      setMirando(false);
    }
  }

  if (abriendo) {
    return (
      <Explorador
        alCerrar={() => setAbriendo(false)}
        alElegir={(ruta) => { setTexto(ruta); setAbriendo(false); comprobar(ruta); }}
      />
    );
  }

  return (
    <section className="carta">
      <h2>Elige el manuscrito<span className="sub">se pide una vez</span></h2>
      <p className="vacio">
        Elige tu manuscrito con el selector de siempre. Los informes y los entregables se
        guardarán en su misma carpeta, junto al texto al que pertenecen.
      </p>

      <div className="dir">
        <button className="btn" onClick={() => abrirNativo("documento")} disabled={mirando || pidiendo}>
          {pidiendo ? "Esperando al selector…" : "Elegir mi manuscrito"}
        </button>
        <button className="btn fantasma" onClick={() => abrirNativo("carpeta")} disabled={mirando || pidiendo}>
          O la carpeta entera
        </button>
      </div>

      {/* El campo se queda, pero de respaldo: teclear una ruta a mano funciona y a veces es más
          rápido —arrastrar la carpeta al campo la pega entera—, pero no puede ser el camino
          principal. */}
      <p className="vacio">O pega la ruta, si la tienes a mano:</p>
      <div className="dir">
        <input
          className="escribir"
          placeholder="/Users/tu-usuario/Documentos/Mi novela"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") comprobar(); }}
        />
        <button className="btn fantasma" onClick={() => comprobar()} disabled={mirando || !texto.trim()}>
          {mirando ? "Mirando…" : "Usar esta"}
        </button>
      </div>
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
    </section>
  );
}
